// LandingPage.tsx
import Head from 'next/head';
import { Layout } from 'components/Layout';
import { useState, useEffect } from 'react';
import { getPageStaticProps } from 'lib/dato-request';
import { NextSeo } from 'next-seo';
import { useForm } from 'react-hook-form';
import { checkValidEmail, submitInfluencerSignup, checkInfluencerAvailability } from 'lib/muzology';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';

// Modal Component
const SignupModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const [loading, setLoading] = useState(false);
    const [apiErrors, setApiErrors] = useState<any>(null);

    const {
        register,
        handleSubmit: handleFormSubmit,
        formState: { errors },
        getValues,
    } = useForm();

    // Validate at least one social handle is filled (only check on tiktok field to avoid duplicate errors)
    const validateSocialHandles = () => {
        const values = getValues();
        const hasSocial = values.facebook?.trim() || values.instagram?.trim() || values.tiktok?.trim();
        return hasSocial ? true : 'At least one social media handle is required';
    };

    const handleSubmit = (data: any) => {
        setLoading(true);
        setApiErrors(null);

        // Transform data to API format
        const socialHandles: any = {};
        if (data.facebook) socialHandles.facebook = data.facebook.trim();
        if (data.instagram) socialHandles.instagram = data.instagram.trim();
        if (data.tiktok) socialHandles.tiktok = data.tiktok.trim();

        const payload = {
            email: data.email.toLowerCase().trim(),
            name: data.name?.trim() || undefined,
            social_handles: socialHandles,
            anonymous_id: undefined, // TODO: Get from RudderStack if available
        };

        console.log('Submitting influencer signup:', payload);

        submitInfluencerSignup(payload)
            .then((response) => {
                console.log('Signup success:', response.data);
                // Redirect to onboarding flow
                if (response.data.redirect) {
                    window.location.assign(response.data.redirect);
                }
            })
            .catch((error) => {
                console.error('Signup error:', error);
                setLoading(false);
                // Map API errors to form
                if (error.response?.data) {
                    setApiErrors(error.response.data);
                }
            });
    };

    if (!isOpen) return null;

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4 py-8 mt-16 sm:mt-0' onClick={onClose}>
            <LoadingOverlay className='fixed' show={loading} />
            {/* Backdrop */}
            <div className='absolute inset-0 bg-black bg-opacity-75 backdrop-blur-sm' />

            {/* Modal */}
            <div
                className='relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 md:p-10 max-w-lg w-full max-h-full overflow-y-auto shadow-2xl border border-gray-200'
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    className='absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 transition-all duration-200 border-none cursor-pointer'
                    aria-label='Close modal'
                >
                    ×
                </button>

                {/* Header with accent */}
                <div className='mb-8'>
                    <div className='w-12 h-1 bg-[#FFD60A] rounded-full mb-4' />
                    <h2 className='text-3xl md:text-4xl font-bold text-gray-900 mb-2'>Start Your Free Trial</h2>
                    <p className='text-gray-600 text-lg'>Join the Muzology FAB and get 30 days of full access!</p>
                </div>

                {/* Form */}
                <form onSubmit={handleFormSubmit(handleSubmit)} className='space-y-5'>
                    <div>
                        <label htmlFor='email' className='block text-gray-800 font-semibold mb-2 text-sm'>
                            Email Address <span className='text-red-500'>*</span>
                        </label>
                        <input
                            type='email'
                            id='email'
                            {...register('email', {
                                required: 'Email is required',
                                validate: { validEmail: checkValidEmail }
                            })}
                            className='w-full px-4 py-3.5 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200'
                            placeholder='your@email.com'
                        />
                        {errors.email && <div className='mt-1 text-red-500 text-sm'>{errors.email.type === 'validEmail' ? 'Invalid email address' : (errors.email.message as string)}</div>}
                        {apiErrors?.email && <div className='mt-1 text-red-500 text-sm'>{apiErrors.email[0]}</div>}
                    </div>

                    <div>
                        <label htmlFor='name' className='block text-gray-800 font-semibold mb-2 text-sm'>
                            Name (Optional)
                        </label>
                        <input
                            type='text'
                            id='name'
                            {...register('name')}
                            className='w-full px-4 py-3.5 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200'
                            placeholder='Your Name'
                        />
                    </div>

                    <div className='pt-2'>
                        <p className='text-sm font-semibold text-gray-700 mb-3'>Social Media Handles <span className='text-red-500'>*</span></p>
                        <p className='text-xs text-gray-600 mb-3'>At least one is required</p>

                        <div className='space-y-3'>
                            <input
                                type='text'
                                id='facebook'
                                {...register('facebook')}
                                className='w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200'
                                placeholder='Facebook: @yourhandle'
                            />

                            <input
                                type='text'
                                id='instagram'
                                {...register('instagram')}
                                className='w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200'
                                placeholder='Instagram: @yourhandle'
                            />

                            <input
                                type='text'
                                id='tiktok'
                                {...register('tiktok', { validate: validateSocialHandles })}
                                className='w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all duration-200'
                                placeholder='TikTok: @yourhandle'
                            />
                        </div>
                        {errors.tiktok && (
                            <div className='mt-2 text-red-500 text-sm'>{errors.tiktok.message as string}</div>
                        )}
                        {apiErrors?.social_handles && <div className='mt-2 text-red-500 text-sm'>{apiErrors.social_handles[0]}</div>}
                    </div>

                    <button
                        type='submit'
                        disabled={loading}
                        className='w-full px-8 py-4 bg-[#FFD60A] text-black font-bold text-lg rounded-xl hover:bg-yellow-400 hover:shadow-lg transition-all duration-300 ease-in-out mt-8 border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        Get Started Free
                    </button>
                </form>
            </div>
        </div>
    );
};

// Circle Arrow Component - points down-right (desktop)
const ArrowDownRight = ({ className = '' }: { className?: string }) => (
    <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M11 15H15M15 15V11M15 15L9 9M21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12Z"
            stroke="#FFD60A"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Circle Arrow Component - points down-left (mobile)
const ArrowDownLeft = ({ className = '' }: { className?: string }) => (
    <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M9 11V15M9 15H13M9 15L15 9M21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12Z"
            stroke="#FFD60A"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Simple Vimeo Embed Component with Loading Spinner
const VimeoEmbed = ({ videoId, className = '' }: { videoId: string; className?: string }) => {
    const [isLoading, setIsLoading] = useState(true);

    return (
        <div className='relative' style={{ paddingBottom: '56.25%', height: 0 }}>
            {isLoading && (
                <div className='absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-90 rounded-lg z-10'>
                    <div
                        className='w-20 h-20 rounded-full animate-spin'
                        style={{
                            border: '4px solid #FFD60A',
                            borderTopColor: 'transparent'
                        }}
                    />
                </div>
            )}
            <iframe
                src={`https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`}
                className={`absolute top-0 left-0 w-full h-full border-0 ${className}`}
                allow='autoplay; fullscreen; picture-in-picture'
                allowFullScreen
                onLoad={() => setIsLoading(false)}
            />
        </div>
    );
};

const FAB = ({ data }) => {
    const { page } = data;
    const { components, seo } = page;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAvailable, setIsAvailable] = useState(true); // Default to true to show buttons initially

    // Check availability on mount
    useEffect(() => {
        checkInfluencerAvailability()
            .then((data) => {
                console.log('Availability check:', data);
                setIsAvailable(data.available !== false);
            })
            .catch((error) => {
                console.error('Availability check failed:', error);
                // On error, keep showing the buttons (fail open)
                setIsAvailable(true);
            });
    }, []);

    return (
        <Layout showClassCodeInput={false} hideNavigationMenu={true} showLoginAndSignup={false} >
            <SignupModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
            <NextSeo title={seo?.title} description={seo?.description} />
            <Head>
                <meta name='keywords' content={page.keywords} />
            </Head>

            {/* Main Container - Tailwind for overall page styling */}
            <div className='min-h-screen bg-home-background text-white font-Lexend relative '>
                {/* Hero Section */}
                <section className='py-16 md:py-24 text-center px-4 md:px-8 relative z-[2] overflow-hidden mx-auto max-w-[1500px]'>
                    <div className='hidden md:block absolute top-[50px] right-0 rounded-full opacity-[0.7] bg-[#B031FA] bg-opacity-[0.5] blur-[250px] w-[729px] h-[585px] z-[1]' />
                    <div className='hidden md:block absolute bottom-[50px] left-0 rounded-full opacity-[0.7] bg-[#161042] bg-opacity-[0.5] blur-[250px] w-[729px] h-[585px] z-[1]' />

                    <div className='container mx-auto z-[2] relative md:flex lg:flex-row md:items-stretch md:gap-8 mt-10'>
                        <div className='md:flex-1 '>
                            <h1 className='text-[32px] md:text-5xl font-normal lg:mb-0 leading-tight max-w-[1500px] mx-auto md:mx-0 z-[2] relative text-center md:text-left'>
                                You're Invited: Come Join The Band
                            </h1>
                            <div className='md:flex-1 ghost-inspector-hide mt-5 sm:mt-10 md:mt-0 max-w-[1000px] mx-auto md:mx-0 block lg:hidden'>
                                <VimeoEmbed videoId='1127855432' className='rounded-lg shadow-lg' />
                            </div>
                            <p className='text-lg md:text-xl lg:text-2xl mb-0 max-w-3xl mx-auto text-left opacity-90 '>
                                Ready to become a rockstar advocate for learning? The Muzology FAB is a community for our most passionate parents who want to
                                share the beat, earn rewards, and help other families discover the magic of music-powered math.
                            </p>
                            {/* CTA with playful arrow */}
                            <div className='mt-8'>
                                {/* Click Here To Start text with responsive arrows */}
                                <div className='flex items-center justify-center md:justify-start gap-2 mb-2'>
                                    <span className='text-2xl md:text-4xl font-bold text-[#FFDB35] whitespace-nowrap'>
                                        Click Here To Start
                                    </span>
                                    {/* Mobile: down-left arrow */}
                                    <ArrowDownLeft className='w-10 h-10 flex-shrink-0 md:hidden' />
                                    {/* Desktop: down-right arrow */}
                                    <ArrowDownRight className='hidden md:block w-12 h-12 flex-shrink-0' />
                                </div>

                                {/* Buttons */}
                                <div className='flex flex-wrap-reverse gap-4 justify-center md:justify-start items-center'>
                                    <a
                                        href='/docs/FAB Requirements.pdf'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='px-8 py-0 text-white font-semibold text-lg rounded-[50px] hover:!bg-white hover:text-black transition-all duration-300 ease-in-out h-[55px] flex items-center justify-center cursor-pointer no-underline'
                                        style={{ backgroundColor: 'transparent', border: '2px solid white' }}
                                    >
                                        Muzology FAB Guide
                                    </a>
                                    {isAvailable ? (
                                        <button
                                            onClick={() => setIsModalOpen(true)}
                                            className='px-8 py-0 bg-[#FFD60A] text-black font-semibold text-lg rounded-[50px] hover:bg-yellow-400 hover:shadow-[0_0_20px_rgba(255,214,10,0.6)] hover:scale-105 transition-all duration-300 ease-in-out border-none h-[55px]'
                                        >
                                            Free 30 Day Trial
                                        </button>
                                    ) : (
                                        <div className='px-8 py-0 bg-gray-400 text-gray-700 font-semibold text-lg rounded-[50px] h-[55px] flex items-center justify-center cursor-not-allowed'>
                                            Slots Filled
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className='md:flex-1 ghost-inspector-hide mt-5 sm:mt-10 md:mt-0 max-w-[1000px] mx-auto md:mx-0 hidden lg:block'>
                            <VimeoEmbed videoId='1127855432' className='rounded-lg shadow-lg' />
                        </div>
                    </div>
                </section>

                {/* Free Resources Section */}
                <section className='py-12 md:py-20 text-white px-4 md:px-8 bg-[rgb(31,8,51)] bg-opacity-50'>
                    <div className='container mx-auto max-w-[1500px]'>
                        <div className='grid lg:grid-cols-2 gap-8 lg:gap-12 items-start mb-12'>
                            {/* Left Column - Video */}
                            <div>
                                <div className='ghost-inspector-hide'>
                                    <VimeoEmbed videoId='1128302343' className='rounded-lg shadow-lg' />
                                </div>
                            </div>

                            {/* Right Column - Video */}
                            <div>
                                <h1 className='text-[32px] md:text-5xl font-normal mb-0 leading-tight max-w-[1500px] mx-auto md:mx-0 z-[2] relative text-center md:text-left '>
                                    Free Resources
                                </h1>
                                <p className='text-lg md:text-xl lg:text-2xl mb-0 max-w-3xl mx-auto text-left opacity-90 '>
                                    We want every parent to have easy access to this amazing math-learning resource for their kids, so we've made it super
                                    simple to help spread the word about Muzology.
                                </p>
                                <div className='flex flex-wrap gap-4 justify-center md:justify-start mt-8'>
                                    <a
                                        href='https://drive.google.com/drive/u/1/folders/1EsY1XK_izB5oHGc_VjpdEZ3wnWZW4kdl'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='px-8 py-0 text-white font-semibold text-lg rounded-[50px] hover:!bg-white hover:text-black transition-all duration-300 ease-in-out h-[55px] flex items-center justify-center cursor-pointer no-underline'
                                        style={{ backgroundColor: 'transparent', border: '2px solid white' }}
                                    >
                                        Resource Library
                                    </a>
                                    <a
                                        href='/docs/Rhythm & Routine.pdf'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='px-8 py-0 bg-[#FFD60A] text-black font-semibold text-lg rounded-[50px] hover:bg-yellow-400 hover:bg-opacity-90 transition-all duration-300 ease-in-out border-none h-[55px] flex items-center justify-center cursor-pointer no-underline'
                                    >
                                        Rhythm & Routine Guide
                                    </a>
                                </div>
                            </div>
                            {/* Resource Buttons */}
                        </div>
                    </div>
                </section>
                {/* Final CTA Section */}
                <section className='py-16 md:py-24 text-white px-4'>
                    <div className='container mx-auto text-center max-w-5xl'>
                        <h2 className='text-3xl md:text-5xl font-normal mb-4'>How to Join the Band (It's Easy!)</h2>
                        <p className='mt-0 text-lg md:text-xl mb-8 opacity-90'>There are no applications or auditions. You join by participating!</p>

                        {/* Steps Container */}
                        <div className='mt-8 space-y-6'>
                            {/* Step 1 */}
                            <div className='flex flex-col md:flex-row items-center  gap-4 md:gap-6 text-center md:text-left'>
                                <div className='w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#FFD60A] flex items-center justify-center flex-shrink-0 mt-0 md:-mt-3'>
                                    <span className='text-3xl md:text-4xl font-bold text-black'>1</span>
                                </div>
                                <div className='flex-1'>
                                    <h3 className='text-2xl md:text-3xl font-semibold mb-2'>Start Creating & Sharing</h3>
                                    <p className='text-lg md:text-xl opacity-90'>Create and share your first three posts, following the 3 golden rules.</p>
                                </div>
                            </div>

                            {/* Step 2 */}
                            <div className='flex flex-col md:flex-row items-center gap-4 md:gap-6 text-center md:text-left'>
                                <div className='w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#FFD60A] flex items-center justify-center flex-shrink-0 mt-0 md:-mt-10'>
                                    <span className='text-3xl md:text-4xl font-bold text-black'>2</span>
                                </div>
                                <div className='flex-1'>
                                    <h3 className='text-2xl md:text-3xl font-semibold mb-2'>Get Your Welcome Kit</h3>
                                    <p className='text-lg md:text-xl opacity-90'>
                                        Once we see your tagged posts, we'll slide into your DMs with a welcome message, your code for a free month, and a link
                                        to your "All-Access Pass" Welcome Kit!
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* CTA Button */}
                        <div className='mt-10 md:mt-12 flex justify-center'>
                            <div>
                                {/* Click Here To Start text with responsive arrows */}
                                <div className='flex items-center gap-2 mb-2 justify-center '>
                                    <span className='text-2xl md:text-4xl font-bold text-[#FFD60A] whitespace-nowrap'>
                                        Click Here To Start
                                    </span>
                                    {/* Mobile: down-left arrow */}
                                    <ArrowDownLeft className='w-10 h-10 flex-shrink-0 md:hidden' />
                                    {/* Desktop: down-right arrow */}
                                    <ArrowDownRight className='hidden md:block w-12 h-12 flex-shrink-0' />
                                </div>

                                {/* Buttons */}
                                <div className='flex flex-wrap gap-4 justify-center md:justify-start items-center'>
                                    <a
                                        href='/docs/FAB Requirements.pdf'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='inline-flex items-center justify-center px-10 py-0 text-white font-semibold text-lg md:text-xl rounded-[50px] h-[55px] hover:!bg-white hover:text-black transition-all duration-300 ease-in-out cursor-pointer no-underline'
                                        style={{ backgroundColor: 'transparent', border: '2px solid white' }}
                                    >
                                        View the FAB Guide
                                    </a>
                                    {isAvailable ? (
                                        <button
                                            onClick={() => setIsModalOpen(true)}
                                            className='px-8 py-0 bg-[#FFD60A] text-black font-semibold text-lg md:text-xl rounded-[50px] hover:bg-yellow-400 hover:shadow-[0_0_20px_rgba(255,214,10,0.6)] hover:scale-105 transition-all duration-300 ease-in-out border-none h-[55px]'
                                        >
                                            Free 30 Day Trial
                                        </button>
                                    ) : (
                                        <div className='px-8 py-0 bg-gray-400 text-gray-700 font-semibold text-lg md:text-xl rounded-[50px] h-[55px] flex items-center justify-center cursor-not-allowed'>
                                            Slots Filled
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className='mt-10 md:mt-12'></div>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export async function getStaticProps({ preview = false }) {
    return getPageStaticProps('FAB');
}

export default FAB;
